import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus, Pencil, Power, Trash2, Upload, Download, FileSpreadsheet,
  Search, X as XIcon, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useIsAdmin } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * System Supplier Mappings — master page for the
 * `System + Supplier SKU → Buyer Code + Buyer Description` table.
 * Powers the "System" dropdown on Supplier Invoice Upload.
 */
export default function SystemMappings() {
  const isAdmin = useIsAdmin();
  const [systems, setSystems] = useState([]);
  const [products, setProducts] = useState([]);
  const [systemFilter, setSystemFilter] = useState('__all__');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadSystems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/system-supplier-mappings/systems`);
      setSystems(res.data?.systems || []);
    } catch (_e) { /* silent */ }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/products`);
      setProducts(res.data || []);
    } catch (_e) { setProducts([]); }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (systemFilter && systemFilter !== '__all__') params.system = systemFilter;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${API_URL}/system-supplier-mappings`, { params });
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [systemFilter, q]);

  useEffect(() => { loadSystems(); loadProducts(); }, [loadSystems, loadProducts]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/system-supplier-mappings/${deleteTarget.id}`);
      toast.success('Mapping deleted');
      setDeleteTarget(null);
      await Promise.all([loadRows(), loadSystems()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API_URL}/system-supplier-mappings/${m.id}`, { active: !m.active });
      toast.success(m.active ? 'Deactivated' : 'Activated');
      await Promise.all([loadRows(), loadSystems()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-slate-600">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4" data-testid="system-mappings-page">
      <div className="flex flex-wrap items-start justify-between gap-3 pb-2 border-b border-slate-200">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">System Mappings</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Map supplier/system SKUs to your internal products and buyer codes.
            The single dedicated place for Supplier SKU → Product → Buyer Code resolution.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="ssm-bulk-btn" className="gap-2 h-9">
            <FileSpreadsheet className="w-4 h-4" /> Bulk Import
          </Button>
          <Button onClick={() => { setEditing(null); setAddOpen(true); }} data-testid="ssm-add-btn" className="btn-primary gap-2 h-9">
            <Plus className="w-4 h-4" /> Add Mapping
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end p-4 rounded-md border border-slate-200 bg-white shadow-sm">
        <div className="space-y-1.5 min-w-[220px]">
          <Label className="text-xs font-medium text-slate-600">System</Label>
          <Select value={systemFilter} onValueChange={setSystemFilter}>
            <SelectTrigger data-testid="ssm-system-filter" className="h-9"><SelectValue placeholder="All systems" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All systems</SelectItem>
              {systems.map(s => <SelectItem key={s} value={s} data-testid={`ssm-system-option-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[260px]">
          <Label className="text-xs font-medium text-slate-600">Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" placeholder="Supplier SKU, Buyer Code, or description…" data-testid="ssm-search" />
          </div>
        </div>
        <div className="text-xs text-slate-500 pb-2 whitespace-nowrap">
          <span className="font-medium text-slate-700 tabular-nums">{rows.length}</span> {rows.length === 1 ? 'mapping' : 'mappings'}{systemFilter !== '__all__' ? ` in ${systemFilter}` : ''}
        </div>
      </div>

      <div className="border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50/70">
            <TableRow className="hover:bg-slate-50/70">
              <TableHead className="w-[130px] font-medium text-slate-600 text-xs uppercase tracking-wide">System</TableHead>
              <TableHead className="w-[200px] font-medium text-slate-600 text-xs uppercase tracking-wide">Supplier SKU</TableHead>
              <TableHead className="w-[220px] font-medium text-slate-600 text-xs uppercase tracking-wide">Product</TableHead>
              <TableHead className="w-[160px] font-medium text-slate-600 text-xs uppercase tracking-wide">Buyer Code</TableHead>
              <TableHead className="font-medium text-slate-600 text-xs uppercase tracking-wide">Buyer Description</TableHead>
              <TableHead className="w-[80px] text-center font-medium text-slate-600 text-xs uppercase tracking-wide">Active</TableHead>
              <TableHead className="w-[130px] text-right font-medium text-slate-600 text-xs uppercase tracking-wide">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6">
                No mappings yet. Use <span className="font-medium">Bulk Import</span> or <span className="font-medium">Add Mapping</span>.
              </TableCell></TableRow>
            ) : rows.map((m) => (
              <TableRow key={m.id} data-testid={`ssm-row-${m.id}`}>
                <TableCell className="font-medium">{m.system}</TableCell>
                <TableCell className="tabular-nums">{m.supplier_sku}</TableCell>
                <TableCell className="text-slate-700 text-sm">
                  {m.product_name ? m.product_name : <span className="text-slate-400 text-xs italic">Not linked</span>}
                </TableCell>
                <TableCell className="tabular-nums font-medium text-slate-800">{m.buyer_item_code}</TableCell>
                <TableCell className="text-slate-600 text-sm">{m.buyer_item_description || '—'}</TableCell>
                <TableCell className="text-center">
                  {m.active
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Yes</Badge>
                    : <Badge variant="secondary" className="bg-slate-100 text-slate-600">No</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button type="button" variant="ghost" size="icon" onClick={() => { setEditing(m); setAddOpen(true); }} title="Edit" data-testid={`ssm-edit-${m.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => toggleActive(m)} title={m.active ? 'Deactivate' : 'Activate'} data-testid={`ssm-toggle-${m.id}`}>
                      <Power className={cn('w-4 h-4', m.active ? 'text-emerald-600' : 'text-slate-400')} />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setDeleteTarget(m)} title="Delete" data-testid={`ssm-delete-${m.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MappingFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        editing={editing}
        systems={systems}
        products={products}
        onSaved={async () => { setAddOpen(false); await Promise.all([loadRows(), loadSystems()]); }}
      />

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={async () => { setBulkOpen(false); await Promise.all([loadRows(), loadSystems()]); }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent data-testid="ssm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete mapping?</DialogTitle>
            <DialogDescription>
              This will permanently remove the mapping for
              <span className="font-medium"> {deleteTarget?.system}</span> + <span className="font-medium">{deleteTarget?.supplier_sku}</span>.
              Supplier invoices using this SKU in this System will fall back to "Mapping Not Found".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDelete} data-testid="ssm-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Add/Edit single mapping dialog ---------------------------------------
const emptyForm = { system: '', supplier_sku: '', product_id: '', buyer_item_code: '', buyer_item_description: '', active: true };
function MappingFormDialog({ open, onOpenChange, editing, systems, products, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [customSystem, setCustomSystem] = useState(false);
  const [productQuery, setProductQuery] = useState('');

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          system: editing.system, supplier_sku: editing.supplier_sku,
          product_id: editing.product_id || '',
          buyer_item_code: editing.buyer_item_code,
          buyer_item_description: editing.buyer_item_description || '',
          active: !!editing.active,
        });
        setCustomSystem(!systems.includes(editing.system));
      } else {
        setForm(emptyForm);
        setCustomSystem(false);
      }
      setProductQuery('');
    }
  }, [open, editing, systems]);

  const filteredProducts = React.useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products.slice(0, 20);
    return products.filter(p => (p.name || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q) || (p.size || '').toLowerCase().includes(q)).slice(0, 30);
  }, [products, productQuery]);

  const selectedProduct = React.useMemo(() => products.find(p => p.id === form.product_id), [products, form.product_id]);

  const submit = async () => {
    if (!form.system.trim() || !form.supplier_sku.trim() || !form.buyer_item_code.trim()) {
      toast.error('System, Supplier SKU and Buyer Code are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, product_id: form.product_id || null };
      if (editing) {
        await axios.put(`${API_URL}/system-supplier-mappings/${editing.id}`, payload);
        toast.success('Mapping updated');
      } else {
        await axios.post(`${API_URL}/system-supplier-mappings`, payload);
        toast.success('Mapping created');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="ssm-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
          <DialogDescription>Maps a Supplier SKU within a System to a Buyer Code + Description.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>System *</Label>
            {customSystem || systems.length === 0 ? (
              <div className="flex gap-2">
                <Input value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} placeholder="e.g., Refirm, Dio" data-testid="ssm-form-system-input" />
                {systems.length > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCustomSystem(false)}>Pick existing</Button>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <Select value={form.system} onValueChange={(v) => setForm({ ...form, system: v })}>
                  <SelectTrigger data-testid="ssm-form-system-select"><SelectValue placeholder="Choose system" /></SelectTrigger>
                  <SelectContent>
                    {systems.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="sm" onClick={() => setCustomSystem(true)}>New system…</Button>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Supplier SKU *</Label>
            <Input value={form.supplier_sku} onChange={(e) => setForm({ ...form, supplier_sku: e.target.value })} placeholder="e.g., REF-IMP-3.5x11.5" data-testid="ssm-form-sku" />
          </div>
          <div className="space-y-1.5">
            <Label>Internal Product <span className="text-xs text-slate-400 font-normal">(optional — links this SKU to your product master)</span></Label>
            {selectedProduct ? (
              <div className="flex items-center justify-between border border-slate-200 rounded-md p-2 bg-slate-50/50">
                <div className="text-sm">
                  <div className="font-medium text-slate-800">{selectedProduct.name}</div>
                  <div className="text-xs text-slate-500">{selectedProduct.brand} · {selectedProduct.size || '—'}</div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, product_id: '' })} data-testid="ssm-form-product-clear">
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={productQuery} onChange={(e) => setProductQuery(e.target.value)} className="pl-8" placeholder="Search by product name, brand, or size…" data-testid="ssm-form-product-search" />
                </div>
                {productQuery && (
                  <div className="max-h-40 overflow-auto border border-slate-200 rounded-md">
                    {filteredProducts.length === 0 ? (
                      <div className="p-2 text-xs text-slate-500">No matches</div>
                    ) : filteredProducts.map(p => (
                      <button key={p.id} type="button" onClick={() => { setForm({ ...form, product_id: p.id }); setProductQuery(''); }}
                        data-testid={`ssm-form-product-option-${p.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 text-sm">
                        <div className="font-medium text-slate-800">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.brand} · {p.size || '—'}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Buyer Code *</Label>
            <Input value={form.buyer_item_code} onChange={(e) => setForm({ ...form, buyer_item_code: e.target.value })} placeholder="e.g., DEN-IMP-024" data-testid="ssm-form-buyer-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Buyer Description</Label>
            <Input value={form.buyer_item_description} onChange={(e) => setForm({ ...form, buyer_item_description: e.target.value })} placeholder="e.g., Implant - Narrow - Medium" data-testid="ssm-form-buyer-desc" />
          </div>
          <div className="flex items-center justify-between rounded-md border bg-white p-3">
            <div>
              <Label className="text-sm font-medium">Active</Label>
              <p className="text-xs text-slate-500">{form.active ? 'Yes — used for resolution' : 'No — hidden from resolution'}</p>
            </div>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ssm-form-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="btn-primary" data-testid="ssm-form-save">
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk Import dialog ---------------------------------------------------
function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  useEffect(() => { if (!open) reset(); }, [open]);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/system-supplier-mappings/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'system_supplier_mappings_template.csv';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast.success('Template downloaded');
    } catch (_e) { toast.error('Failed to download template'); }
  };

  const runPreview = async () => {
    if (!file) { toast.error('Pick a CSV or Excel file first'); return; }
    setPreviewing(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/system-supplier-mappings/bulk-preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      if (res.data.summary.valid === 0) toast.error('No valid rows to import');
      else toast.success(`${res.data.summary.valid} of ${res.data.summary.total} row(s) ready`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const runCommit = async () => {
    if (!preview) return;
    // Only commit rows that will actually create a new mapping. Duplicates,
    // existing matches, conflicts and errors are all excluded from the commit.
    const okRows = preview.rows.filter(r => r.status === 'ok').map(r => ({
      system: r.system, supplier_sku: r.supplier_sku,
      product_id: r.product_id || null,
      buyer_item_code: r.buyer_item_code, buyer_item_description: r.buyer_item_description,
    }));
    if (!okRows.length) { toast.error('No new mappings to import'); return; }
    setCommitting(true);
    try {
      const res = await axios.post(`${API_URL}/system-supplier-mappings/bulk-commit`, { rows: okRows });
      setResult(res.data);
      if (res.data.summary.created > 0) toast.success(`${res.data.summary.created} mapping(s) created`);
      if (res.data.summary.failed > 0) toast.error(`${res.data.summary.failed} row(s) failed`);
      if (onImported && res.data.summary.created > 0) await onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ssm-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-teal-600" /> Bulk Import System-Supplier Mappings
          </DialogTitle>
          <DialogDescription>
            Upload a CSV/Excel with columns:
            <span className="font-medium"> System, Supplier SKU, Buyer Code, Buyer Description</span>
            <span className="text-slate-500"> (Product SKU or Name is optional).</span>
            Rows are classified as <span className="font-medium">new</span>, <span className="font-medium">duplicate</span> (skipped), <span className="font-medium">existing</span> (skipped), or <span className="font-medium">conflict</span> (needs review). Only new rows are imported.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between gap-3 rounded-md border bg-slate-50 p-3">
            <div className="text-sm">
              <p className="font-medium">Need the template?</p>
              <p className="text-xs text-slate-500">CSV with required columns and two sample rows.</p>
            </div>
            <Button variant="outline" onClick={downloadTemplate} data-testid="ssm-bulk-template-btn"><Download className="w-4 h-4 mr-2" /> Download Template</Button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { reset(); const f = e.target.files?.[0]; if (f) setFile(f); }} className="hidden" data-testid="ssm-bulk-file" />
                <span className="inline-flex items-center px-3 py-2 rounded-md border bg-white text-sm hover:bg-slate-50"><Upload className="w-4 h-4 mr-2" /> Choose file</span>
              </label>
              <span className="text-xs text-slate-600">{file ? file.name : 'No file selected'}</span>
            </div>
            <Button onClick={runPreview} disabled={!file || previewing} className="btn-primary" data-testid="ssm-bulk-preview-btn">
              {previewing ? 'Previewing…' : 'Preview'}
            </Button>
          </div>
          {preview && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {preview.summary.valid} new</Badge>
                {(preview.summary.existing_match ?? 0) > 0 && (
                  <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">{preview.summary.existing_match} existing (skipped)</Badge>
                )}
                {(preview.summary.duplicate_input ?? 0) > 0 && (
                  <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">{preview.summary.duplicate_input} duplicate in file (skipped)</Badge>
                )}
                {(preview.summary.conflict ?? 0) > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {preview.summary.conflict} conflict</Badge>
                )}
                {(preview.summary.error ?? 0) > 0 && (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {preview.summary.error} error</Badge>
                )}
                {(preview.summary.product_linked ?? 0) > 0 && (
                  <Badge variant="outline" className="border-teal-300 text-teal-700">{preview.summary.product_linked} Product linked</Badge>
                )}
                {(preview.summary.product_unlinked ?? 0) > 0 && (
                  <Badge variant="outline" className="border-slate-300 text-slate-500">{preview.summary.product_unlinked} Product not linked</Badge>
                )}
                <span className="text-xs text-slate-500">of {preview.summary.total} row(s)</span>
              </div>
              <ScrollArea className="h-[320px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Row</TableHead>
                      <TableHead>System</TableHead>
                      <TableHead>Supplier SKU</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Buyer Code</TableHead>
                      <TableHead>Buyer Description</TableHead>
                      <TableHead className="w-[110px]">Status</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map(r => {
                      const badge =
                        r.status === 'ok' ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">New</Badge> :
                        r.status === 'duplicate_input' ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Duplicate</Badge> :
                        r.status === 'existing_match' ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Existing</Badge> :
                        r.status === 'conflict' ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Conflict</Badge> :
                        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Error</Badge>;
                      const rowBg = r.status === 'error' ? 'bg-rose-50/60'
                        : r.status === 'conflict' ? 'bg-amber-50/60'
                        : (r.status === 'duplicate_input' || r.status === 'existing_match') ? 'bg-slate-50/50'
                        : '';
                      return (
                        <TableRow key={r.row_number} className={cn(rowBg)}>
                          <TableCell className="tabular-nums">{r.row_number}</TableCell>
                          <TableCell>{r.system || '—'}</TableCell>
                          <TableCell className="tabular-nums">{r.supplier_sku || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {r.product_id
                              ? <span className="text-teal-700">✓ {r.product_name || r.product_id}</span>
                              : r.product_lookup
                                ? <span className="text-slate-500 italic">not linked</span>
                                : <span className="text-slate-400">—</span>}
                          </TableCell>
                          <TableCell className="tabular-nums">{r.buyer_item_code || '—'}</TableCell>
                          <TableCell className="text-slate-600">{r.buyer_item_description || '—'}</TableCell>
                          <TableCell>{badge}</TableCell>
                          <TableCell>
                            {(r.errors && r.errors.length > 0) && (
                              <ul className="text-xs text-rose-700 list-disc pl-4">
                                {r.errors.map((e, i) => <li key={`e${i}`}>{e}</li>)}
                              </ul>
                            )}
                            {(r.warnings && r.warnings.length > 0) && (
                              <ul className="text-xs text-amber-700 list-disc pl-4">
                                {r.warnings.map((w, i) => <li key={`w${i}`}>{w}</li>)}
                              </ul>
                            )}
                            {(!r.errors || r.errors.length === 0) && (!r.warnings || r.warnings.length === 0) && (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
          {result && (
            <div className="rounded-md border p-3 bg-slate-50">
              <div className="text-sm">
                Attempted: <span className="font-medium">{result.summary.attempted}</span> •
                Created: <span className="font-medium text-emerald-700">{result.summary.created}</span> •
                Failed: <span className="font-medium text-rose-700">{result.summary.failed}</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={runCommit} disabled={!preview || preview.summary.valid === 0 || committing || !!result} className="btn-primary" data-testid="ssm-bulk-commit-btn">
            {committing ? 'Importing…' : preview ? `Import ${preview.summary.valid} new row(s)` : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
